import React, { useEffect, useState } from "react";
import { ArrowRight, Check, ChevronRight, Minus, Plus, X } from "lucide-react";
import { api } from "./api";
import { Badge, Cellule, Modale, Puces, Recherche, Section, Tableau } from "./ui";

/* Facturation → Cartes 10 cours : TOUTES les cartes au même endroit, qu'elles se paient au
   comptoir ou sur la facture du semestre (Jen, 30.10.2026 : une carte passée sur la facture
   « disparaissait » de Cash / espèces, qui ne montre que le comptoir). La carte appartient à
   l'élève et à son cours ; la colonne Paiement dit comment elle se paie, avec le lien vers
   la caisse ou vers la facture.
   Carte 10 numérique (version de test d'abord) : en plus, chaque carte décomptée toute seule
   par l'appel (un « présent » = une séance) ; terminée, la famille reçoit un e-mail ;
   « hors carte » : des séances au-delà de la carte, une nouvelle carte est due.
   Pointage (Jen, 03.11.2026 : « une carte ? un calendrier ? ») : chaque carte est une tuile
   avec ses 10 cases ; la toucher ouvre la carte comme sur papier (10 cases tamponnées, les
   cases d'avant l'app en −/+) et le calendrier de ses cours : toucher un cours le pointe. */

const jour = (iso) => (iso ? iso.split("-").reverse().join(".") : "");
const court = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : "");
const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const dateLongue = (iso) => {
  const [a, m, j] = iso.split("-").map(Number);
  return new Date(a, m - 1, j).toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long" });
};
const aujourdhui = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Zurich" });
// L'instant (UTC) écrit par le serveur, lu à l'heure suisse : après minuit, ce n'est plus la veille.
const dateSuisse = (instant) => {
  const d = new Date(instant);
  return Number.isNaN(d.getTime()) ? jour(String(instant).slice(0, 10))
    : d.toLocaleDateString("fr-CH", { timeZone: "Europe/Zurich", day: "2-digit", month: "2-digit", year: "numeric" });
};
const chf = (n) => `${Number(n || 0).toLocaleString("fr-CH", { maximumFractionDigits: 2 })}.–`;
const s = (n) => (n > 1 ? "s" : "");
const ETATS = { en_cours: ["En cours", "vert"], terminee: ["Terminée", "ambre"], expiree: ["Expirée", "neutre"] };
const VERT = "#0D9488";
const AMBRE = "#F59E0B";
const ROUGE = "#DC2626";

/* Le paiement d'une carte, en un badge et une petite ligne. */
function Paiement({ p, onAller }) {
  if (p.mode === "facture") {
    const payee = /pay/i.test(p.statut_facture || "");
    return (
      <div>
        <Badge ton={payee ? "vert" : "bleu"}>Sur la facture{p.statut_facture ? ` · ${p.statut_facture.toLowerCase()}` : ""}</Badge>
        <div className="text-[11px] text-[var(--muted)] mt-1">
          {chf(p.montant)} avec la facture du semestre{p.envoyee_le ? `, envoyée le ${jour(p.envoyee_le)}` : ""}
          {p.cible && onAller && (
            <> · <button type="button" className="underline" onClick={() => onAller({ onglet: "facturation", id: p.cible })}>voir la facture</button></>
          )}
        </div>
      </div>
    );
  }
  if (p.montant === null || p.montant === undefined) return <Badge ton="neutre">Comptoir</Badge>;
  const reste = Number(p.reste || 0);
  const etat = reste <= 0.02 ? ["Comptoir · encaissée", "vert"]
    : Number(p.recu || 0) > 0.02 ? ["Comptoir · payée en partie", "bleu"] : ["Comptoir · à encaisser", "ambre"];
  return (
    <div>
      <Badge ton={etat[1]}>{etat[0]}</Badge>
      <div className="text-[11px] text-[var(--muted)] mt-1">
        {reste <= 0.02 ? `${chf(p.montant)} reçus` : Number(p.recu || 0) > 0.02
          ? `${chf(p.recu)} reçus sur ${chf(p.montant)} · reste ${chf(reste)}` : chf(reste)}
        {onAller && reste > 0.02 && (
          <> · <button type="button" className="underline" onClick={() => onAller({ onglet: "facturation", id: "especes" })}>encaisser</button></>
        )}
      </div>
    </div>
  );
}

/* Le paiement en quelques mots, pour la tuile. */
const paiementCourt = (p = {}) => {
  if (p.mode === "facture") return /pay/i.test(p.statut_facture || "") ? "Facture payée" : "Sur la facture";
  if (p.montant === null || p.montant === undefined) return "Comptoir";
  return Number(p.reste || 0) <= 0.02 ? "Encaissée" : `À encaisser ${chf(p.reste)}`;
};

const faitesDe = (c) => c.faites ?? (c.utilisees || []).length;
const cle = (c) => `${c.eleve_id}|${c.cours_id}|${c.fin}`;
const bientotFinie = (c) => c.statut === "en_cours" && !c.a_suivre && c.restantes <= 2;
const etatDe = (c) => {
  if (c.a_suivre && c.statut === "en_cours") return ["À suivre", "neutre"];
  if (bientotFinie(c)) return [`Plus que ${c.restantes}`, "ambre"];
  return ETATS[c.statut] || ["", "neutre"];
};

/* Les 10 cases en petit (la tuile) : pleines = faites, pâles = d'avant l'app, rouges = hors carte. */
function Cases({ c }) {
  const n = faitesDe(c);
  const pleine = c.statut === "terminee";
  return (
    <span className="flex items-center gap-[5px] flex-wrap" aria-label={`${n} séance${s(n)} sur ${c.nb}`}>
      {Array.from({ length: c.nb }).map((_, k) => {
        const fait = k < n;
        const teinte = pleine ? AMBRE : VERT;
        return (
          <span key={k} className="h-3 w-3 rounded-full" style={{
            background: fait ? teinte : "#fff", opacity: fait && k < (c.deja_faites || 0) ? 0.5 : 1,
            border: `1.5px solid ${fait ? teinte : "#CBD5E1"}`,
          }} />
        );
      })}
      {(c.hors_carte || []).map((d) => (
        <span key={d} className="h-3 w-3 rounded-full" style={{ background: ROUGE }} title={`Hors carte · ${court(d)}`} />
      ))}
    </span>
  );
}

/* La tuile d'une carte : qui, quel cours, les 10 cases, la dernière venue et le paiement. */
function Tuile({ c, reprise, onOuvrir }) {
  const n = faitesDe(c);
  const [label, ton] = etatDe(c);
  const derniere = [...(c.utilisees || []), ...(c.hors_carte || [])].sort().pop();
  return (
    <button type="button" onClick={onOuvrir} data-testid={`cartes10-tuile-${c.inscription_id}`}
      className="group w-full text-left rounded-2xl border bg-white p-4 transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2"
      style={{ borderColor: (c.hors_carte || []).length ? "#FECACA" : "var(--border)" }}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">{c.eleve || "—"}</div>
          <div className="text-xs text-[var(--muted)] truncate">
            {c.cours}{c.jour ? ` · ${c.jour}` : ""}{c.nb_cartes > 1 ? ` · carte n° ${c.rang}` : ""}
          </div>
        </div>
        <span className="shrink-0 inline-flex flex-col items-end gap-1">
          <Badge ton={ton}>{label}</Badge>
          {(c.hors_carte || []).length > 0 && <Badge ton="rouge">{c.hors_carte.length} hors carte</Badge>}
          {reprise && !c.reprise_le && c.statut !== "expiree" && <Badge ton="bleu">À reprendre</Badge>}
        </span>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Cases c={c} />
        <span className="ml-auto tabular-nums text-sm font-semibold whitespace-nowrap">{n}/{c.nb}</span>
      </div>
      <div className="mt-2.5 flex items-center gap-2 text-[11px] text-[var(--muted)]">
        <span className="truncate">
          {derniere ? `Dernière venue ${court(derniere)}` : "Pas encore de venue"} · {paiementCourt(c.paiement)}
        </span>
        <ChevronRight size={14} className="ml-auto shrink-0 opacity-50 group-hover:opacity-100" />
      </div>
    </button>
  );
}

/* La carte comme sur papier : 10 grandes cases, tamponnées avec la date du cours (ou
   « papier » pour celles d'avant l'app). « Cases tamponnées » −/+ : on compte les cases de la
   carte papier, celles cochées à l'appel comprises (elles ne peuvent pas être retirées ici :
   dans le calendrier). */
function CartePapier({ c, plusieurs, occupe, onCases }) {
  const n = faitesDe(c);
  const deja = c.deja_faites || 0;
  const appel = (c.utilisees || []).length;
  const [label, ton] = etatDe(c);
  const teinte = c.statut === "terminee" ? AMBRE : VERT;
  return (
    <div className="rounded-2xl p-4" style={{ background: c.statut === "terminee" ? "#FFFBEB" : "#F0FDFA" }}
      data-testid={`cartes10-papier-${c.inscription_id}`}>
      <div className="flex items-center gap-2">
        <div className="text-sm font-semibold">{plusieurs ? `Carte n° ${c.rang}` : "Carte 10 cours"}</div>
        <span className="tabular-nums text-sm text-[var(--muted)]">{n}/{c.nb}</span>
        <span className="ml-auto"><Badge ton={ton}>{label}</Badge></span>
      </div>
      <div className="grid grid-cols-5 gap-2 mt-3 max-w-[320px]">
        {Array.from({ length: c.nb }).map((_, k) => {
          const papier = k < deja;
          const le = papier ? "" : (c.utilisees || [])[k - deja];
          const fait = papier || !!le;
          return (
            <div key={k} className="aspect-square rounded-full flex flex-col items-center justify-center select-none"
              title={papier ? "Faite avant l'application (carte papier)" : le ? dateLongue(le) : `Case ${k + 1}`}
              style={{
                background: fait ? teinte : "#fff", color: fait ? "#fff" : "#94A3B8",
                opacity: papier ? 0.6 : 1, border: fait ? "none" : "1.5px dashed #CBD5E1",
              }}>
              {fait ? (
                <>
                  <Check size={15} strokeWidth={3} />
                  <span className="text-[10px] font-semibold leading-none mt-0.5">{papier ? "papier" : court(le)}</span>
                </>
              ) : <span className="text-sm font-semibold tabular-nums">{k + 1}</span>}
            </div>
          );
        })}
      </div>
      {/* Les cases tamponnées sur la carte papier (Jen, 30.10.2026 puis 03.11.2026). */}
      <div className="mt-4 flex items-center gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium">Cases tamponnées</div>
          <div className="text-[11px] text-[var(--muted)]">
            {appel ? `comme sur la carte papier · dont ${appel} cochée${s(appel)} à l'appel` : "comme sur la carte papier"}
          </div>
        </div>
        <div className="ml-auto inline-flex items-center rounded-full bg-white border" style={{ borderColor: "var(--border)" }}>
          <button type="button" aria-label="Une case de moins" disabled={occupe || n <= appel}
            onClick={() => onCases(c, n - 1)} data-testid={`cartes10-cases-moins-${c.inscription_id}`}
            className="w-9 h-9 flex items-center justify-center rounded-full disabled:opacity-30"><Minus size={15} /></button>
          <span className="w-7 text-center tabular-nums font-semibold" data-testid={`cartes10-cases-${c.inscription_id}`}>{n}</span>
          <button type="button" aria-label="Une case de plus" disabled={occupe || n >= c.nb}
            onClick={() => onCases(c, n + 1)} data-testid={`cartes10-cases-plus-${c.inscription_id}`}
            className="w-9 h-9 flex items-center justify-center rounded-full disabled:opacity-30"><Plus size={15} /></button>
        </div>
      </div>
      {c.prevenue_le && <div className="text-[11px] text-[var(--muted)] mt-2">Famille prévenue le {dateSuisse(c.prevenue_le)} (carte terminée).</div>}
    </div>
  );
}

/* Un cours du calendrier : venu (vert), hors carte (rouge), absent, sans appel, à venir. */
function Pastille({ x, plusieurs, confirmer, occupe, onToucher }) {
  const j = Number(x.le.slice(8, 10));
  const venu = x.etat === "present";
  const aujour = x.le === aujourdhui();
  if (confirmer) {
    return (
      <button type="button" disabled={occupe} onClick={() => onToucher(x)} data-testid={`cartes10-retirer-${x.le}`}
        className="h-12 px-3 rounded-xl text-[12px] font-semibold text-white" style={{ background: ROUGE }}>
        Retirer le {court(x.le)} ?
      </button>
    );
  }
  let style = { background: "#fff", color: "var(--text)", border: "1.5px dashed #94A3B8" };
  let bas = <Plus size={12} strokeWidth={2.5} />;
  let titre = `${dateLongue(x.le)} : pas coché à l'appel — toucher pour pointer la venue`;
  if (x.a_venir && !venu) {
    style = { background: "#F8FAFC", color: "#CBD5E1", border: "1.5px solid transparent" };
    bas = <span className="text-[9px]">à venir</span>;
    titre = `${dateLongue(x.le)} : à venir`;
  } else if (venu && x.hors) {
    style = { background: "#FEE2E2", color: ROUGE, border: `1.5px solid ${ROUGE}` };
    bas = <span className="text-[9px] font-semibold">hors</span>;
    titre = `${dateLongue(x.le)} : venu, hors carte — toucher pour retirer`;
  } else if (venu) {
    style = { background: VERT, color: "#fff", border: `1.5px solid ${VERT}` };
    bas = plusieurs && x.rang ? <span className="text-[9px] font-semibold">n° {x.rang}</span> : <Check size={12} strokeWidth={3} />;
    titre = `${dateLongue(x.le)} : venu${x.rang ? ` (carte n° ${x.rang})` : ""} — toucher pour retirer`;
  } else if (x.etat === "absent") {
    style = { background: "#F1F5F9", color: "#64748B", border: "1.5px solid #E2E8F0" };
    bas = <X size={12} strokeWidth={2.5} />;
    titre = `${dateLongue(x.le)} : absent à l'appel — toucher pour pointer la venue`;
  }
  return (
    <button type="button" disabled={(x.a_venir && !venu) || occupe} onClick={() => onToucher(x)} title={titre} aria-label={titre}
      data-testid={`cartes10-cours-${x.le}`}
      className="w-12 h-12 rounded-xl flex flex-col items-center justify-center leading-none transition enabled:hover:-translate-y-0.5 disabled:cursor-default"
      style={{ ...style, boxShadow: aujour ? "0 0 0 2px #fff, 0 0 0 4px #0F172A" : "none" }}>
      <span className="text-[15px] font-semibold tabular-nums">{j}</span>
      <span className="mt-1 flex items-center">{bas}</span>
    </button>
  );
}

function Calendrier({ seances, plusieurs, confirmer, occupe, onToucher }) {
  const mois = [];
  for (const x of seances) {
    const m = x.le.slice(0, 7);
    let g = mois.find((y) => y.m === m);
    if (!g) mois.push(g = { m, liste: [] });
    g.liste.push(x);
  }
  if (!mois.length) return <p className="text-sm text-[var(--muted)]">Pas de cours au calendrier pour cette carte.</p>;
  return (
    <div className="space-y-3" data-testid="cartes10-calendrier">
      {mois.map((g) => (
        <div key={g.m}>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)] mb-1.5">
            {MOIS[Number(g.m.slice(5, 7)) - 1]}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {g.liste.map((x) => (
              <Pastille key={x.le} x={x} plusieurs={plusieurs} confirmer={confirmer === x.le} occupe={occupe} onToucher={onToucher} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const Legende = () => (
  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[var(--muted)]">
    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded" style={{ background: VERT }} />venu</span>
    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded" style={{ background: "#FEE2E2", border: `1px solid ${ROUGE}` }} />hors carte</span>
    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded" style={{ background: "#F1F5F9", border: "1px solid #E2E8F0" }} />absent</span>
    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded" style={{ border: "1px dashed #94A3B8" }} />pas coché</span>
  </div>
);

/* La carte ouverte : la (ou les) carte(s) de l'élève à ce cours, et le calendrier de ses cours. */
function Feuille({ groupe, occupe, erreur, pointe, reprise, onFermer, onPointer, onCases, onReprendre, onSuivante, onAnnuler, onAller }) {
  const [confirmer, setConfirmer] = useState("");
  const c = groupe.find((x) => x.courante) || groupe[groupe.length - 1];
  const plusieurs = groupe.length > 1;
  const hors = c.hors_carte?.length ? c.hors_carte : groupe.flatMap((x) => x.hors_carte || []);
  const toucher = (x) => {
    if (x.etat !== "present") { setConfirmer(""); onPointer(c, x.le, true); return; }
    if (confirmer === x.le) { setConfirmer(""); onPointer(c, x.le, false); return; }
    setConfirmer(x.le);
  };
  return (
    <Modale large titre={c.eleve || "Carte 10 cours"} onFermer={onFermer} testid="cartes10-feuille"
      sous_titre={`${c.cours}${c.jour ? ` · le ${c.jour.toLowerCase()}` : ""}${c.fin ? ` · valable jusqu'au ${jour(c.fin)}` : ""}`}>
      <div className="space-y-4">
        {erreur && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }}>{erreur}</div>}
        {pointe && (
          <div className="rounded-2xl px-4 py-2.5 text-sm flex flex-wrap items-center gap-2" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }} data-testid="cartes10-pointe">
            <Check size={15} /> {pointe.present ? `Venue du ${court(pointe.le)} pointée.` : `Venue du ${court(pointe.le)} retirée.`}
            <button type="button" className="ml-auto underline font-medium" onClick={onAnnuler} data-testid="cartes10-annuler-pointage">Annuler</button>
          </div>
        )}
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-3">
            {groupe.map((x) => <CartePapier key={x.inscription_id} c={x} plusieurs={plusieurs} occupe={occupe} onCases={onCases} />)}
            {hors.length > 0 && (
              <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }}>
                <b>{hors.length} cours hors carte</b> ({hors.map(court).join(", ")}) : une nouvelle carte est due.
              </div>
            )}
            {/* Reprise des cartes papier (avant le lancement, Jen, 03.11.2026) : compter, valider, suivante. */}
            {reprise && (groupe.some((x) => !x.reprise_le) ? (
              <div className="rounded-2xl border p-3 flex flex-wrap items-center gap-3" style={{ borderColor: "#BFDBFE", background: "#EFF6FF" }}>
                <div className="text-[12px] text-[#1E40AF] min-w-0 flex-1">Les cases correspondent à la carte papier ?</div>
                <button type="button" disabled={occupe} onClick={() => onReprendre(groupe, true)} data-testid="cartes10-valider"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "#2563EB" }}>
                  Valider · carte suivante
                </button>
              </div>
            ) : (
              <div className="rounded-2xl border p-3 flex flex-wrap items-center gap-2 text-[12px]" style={{ borderColor: "#BBF7D0", background: "#F0FDF4", color: "#15803D" }}
                data-testid="cartes10-reprise-faite">
                <Check size={14} /> Carte papier reprise le {dateSuisse(groupe.map((x) => x.reprise_le).sort().pop())}
                <button type="button" className="underline text-[var(--muted)]" disabled={occupe} onClick={() => onReprendre(groupe, false)}>à revoir</button>
                <button type="button" disabled={occupe} onClick={() => onSuivante(groupe)} data-testid="cartes10-suivante"
                  className="ml-auto rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "#2563EB" }}>
                  Carte suivante
                </button>
              </div>
            ))}
          </div>
          <div onMouseLeave={() => setConfirmer("")}>
            <div className="flex items-baseline gap-2">
              <div className="text-sm font-semibold">Ses cours</div>
              <div className="text-[11px] text-[var(--muted)]">toucher un cours pour le pointer</div>
            </div>
            <div className="mt-1.5 mb-3"><Legende /></div>
            <Calendrier seances={c.seances || []} plusieurs={plusieurs} confirmer={confirmer} occupe={occupe} onToucher={toucher} />
          </div>
        </div>
        <div className="border-t pt-4 space-y-2" style={{ borderColor: "var(--border)" }}>
          {groupe.map((x) => (
            <div key={x.inscription_id} className="flex flex-wrap items-start gap-3">
              <div className="text-sm font-medium w-28 shrink-0">{plusieurs ? `Paiement n° ${x.rang}` : "Paiement"}</div>
              <Paiement p={x.paiement || {}} onAller={onAller ? (cible) => { onFermer(); onAller(cible); } : null} />
            </div>
          ))}
        </div>
      </div>
    </Modale>
  );
}

export default function CartesDix({ onAller, integre = false }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [occupe, setOccupe] = useState(false);
  // La carte ouverte (élève + cours + semestre) et le dernier pointage (pour « Annuler »).
  const [ouverte, setOuverte] = useState(null);
  const [pointe, setPointe] = useState(null);
  const [message, setMessage] = useState("");
  const appeler = async (chemin, options) => {
    setErreur(""); setOccupe(true);
    try { const r = await api(chemin, options); setD(r); return r; } catch (e) { setErreur(e.message); return null; } finally { setOccupe(false); }
  };
  // Pointer (ou retirer) une venue : c'est l'appel de la séance, comme dans l'app du prof.
  const pointer = async (c, le, present = true) => {
    if (await appeler(`/carte-10-numerique/${c.inscription_id}/pointer`, { method: "POST", body: JSON.stringify({ le, present }) })) {
      setPointe({ c, le, present });
    }
  };
  // Les cases tamponnées (Jen, 30.10.2026 : la carte papier) : le total, les présences cochées
  // à l'appel comprises ; le serveur garde la différence (« papier »).
  const cases = async (c, total) => {
    setPointe(null);
    await appeler(`/carte-10-numerique/${c.inscription_id}/faites`, { method: "PUT", body: JSON.stringify({ faites: Number(total) }) });
  };
  // Reprise des cartes papier : « Valider » puis la carte suivante à reprendre.
  const reprendre = async (g, oui) => {
    setPointe(null);
    let r = null;
    for (const x of g.filter((y) => !!y.reprise_le !== oui)) {
      r = await appeler(`/carte-10-numerique/${x.inscription_id}/reprise`, { method: "POST", body: JSON.stringify({ reprise: oui }) });
      if (!r) return;
    }
    if (oui) suivante(g, r);
  };
  // La carte suivante à reprendre (dans l'ordre de la liste) ; aucune : c'est fini.
  const suivante = (g, donnees = d) => {
    const ici = cle(g[0]);
    const prochaine = donnees.cartes.find((c) => cle(c) !== ici && !c.reprise_le && c.statut !== "expiree");
    if (prochaine) { setPointe(null); setErreur(""); setOuverte(cle(prochaine)); return; }
    setOuverte(null);
    setMessage("Toutes les cartes papier sont reprises : la carte numérique est prête à être lancée.");
  };
  const [filtre, setFiltre] = useState("tous");
  const [q, setQ] = useState("");
  useEffect(() => {
    api("/carte-10-numerique").then(setD).catch((e) => setErreur(e.message));
  }, []);
  if (!d) return <p className="text-[var(--muted)]">{erreur || "Chargement…"}</p>;
  const garde = (c) => {
    const p = c.paiement || {};
    if (filtre === "comptoir") return p.mode === "comptoir";
    if (filtre === "facture") return p.mode === "facture";
    if (filtre === "a_encaisser") return p.mode === "comptoir" && Number(p.reste || 0) > 0.02;
    if (filtre === "hors") return (c.hors_carte || []).length > 0;
    if (filtre === "bientot") return bientotFinie(c);
    if (filtre === "a_reprendre") return !c.reprise_le && c.statut !== "expiree";
    if (filtre === "en_cours" || filtre === "terminee") return c.statut === filtre;
    return true;
  };
  const lignes = d.cartes.filter((c) => garde(c)
    && (!q.trim() || `${c.eleve} ${c.cours}`.toLowerCase().includes(q.trim().toLowerCase())));
  const options = [
    { valeur: "tous", label: `Toutes (${d.cartes.length})` },
    ...(d.reprise ? [{ valeur: "a_reprendre", label: `À reprendre (${d.totaux.a_reprendre})` }] : []),
    ...(d.numerique ? [
      { valeur: "en_cours", label: `En cours (${d.totaux.en_cours})` },
      { valeur: "bientot", label: `Bientôt finies (${d.cartes.filter(bientotFinie).length})` },
      { valeur: "hors", label: `Hors carte (${d.cartes.filter((c) => c.hors_carte.length).length})` },
      { valeur: "terminee", label: `Terminées (${d.totaux.terminees})` },
    ] : []),
    { valeur: "comptoir", label: `Au comptoir (${d.totaux.comptoir})` },
    { valeur: "facture", label: `Sur facture (${d.totaux.sur_facture})` },
    { valeur: "a_encaisser", label: `À encaisser (${d.totaux.a_encaisser})` },
  ];
  const groupe = ouverte ? d.cartes.filter((c) => cle(c) === ouverte).sort((a, b) => a.rang - b.rang) : [];
  const fermer = () => { setOuverte(null); setPointe(null); setErreur(""); };
  const aReprendre = d.reprise ? d.cartes.filter((c) => c.statut !== "expiree") : [];
  const reprises = aReprendre.filter((c) => c.reprise_le).length;
  const premiere = d.cartes.find((c) => !c.reprise_le && c.statut !== "expiree");
  return (
    <div className="space-y-6" data-testid="console-cartes10">
      <div>
        {/* Dans Facturation (à côté des parrainages), le titre est déjà sur la pastille. */}
        {!integre && <h1 className="font-display text-3xl tracking-tight">Cartes 10 cours</h1>}
        {/* Court (Jen : « Apple style ») : le détail est dans l'aide de la liste et le guide. */}
        <p className={`text-sm text-[var(--muted)] ${integre ? "" : "mt-1"}`}>
          Toutes les cartes de la saison, payées au comptoir ou avec la facture du semestre.
          {d.numerique && " Chaque « présent » à l'appel tamponne une case."}
        </p>
      </div>
      {erreur && !groupe.length && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }}>{erreur}</div>}
      {message && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }} data-testid="cartes10-message">{message}</div>}
      {/* Avant le lancement (Jen, 03.11.2026) : l'école reprend chaque carte papier, puis on lance. */}
      {d.reprise && aReprendre.length > 0 && (
        <div className="rounded-2xl border p-4" style={{ borderColor: "#BFDBFE", background: "#EFF6FF" }} data-testid="cartes10-reprise">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-[#1E3A8A]">Reprise des cartes papier · {reprises}/{aReprendre.length}</div>
              <div className="text-xs text-[#1E40AF] mt-0.5">
                {premiere ? "Pour chaque carte : compte les cases tamponnées, puis « Valider ». Les familles ne voient rien avant le lancement."
                  : "Toutes les cartes sont reprises : prêt pour le lancement."}
              </div>
            </div>
            {premiere && (
              <button type="button" onClick={() => { setMessage(""); setPointe(null); setErreur(""); setOuverte(cle(premiere)); }} data-testid="cartes10-reprise-go"
                className="rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: "#2563EB" }}>
                {reprises ? "Continuer" : "Commencer"}
              </button>
            )}
          </div>
          <div className="h-1.5 rounded-full mt-3" style={{ background: "#DBEAFE" }}>
            <div className="h-1.5 rounded-full" style={{ width: `${(reprises / aReprendre.length) * 100}%`, background: "#2563EB" }} />
          </div>
        </div>
      )}
      <Section titre={`${d.cartes.length} carte${s(d.cartes.length)}`} testid="cartes10-liste"
        action={<Recherche valeur={q} onChange={setQ} placeholder="Élève ou cours" testid="cartes10-recherche" />}
        aide={d.numerique
          ? "Touche une carte pour la pointer : ses 10 cases, les cases tamponnées et le calendrier de ses cours. Le mode de paiement se choisit sur la fiche de l'élève."
          : "Le mode de paiement se choisit sur la fiche de l'élève (Carte 10 cours → au comptoir ou sur la facture du semestre). Une carte compte pour une demi-personne dans la paie du prof."}>
        <Puces testid="cartes10-filtres" valeur={filtre} onChange={setFiltre} options={options} />
        <div className="mt-4">
          {d.numerique ? (
            lignes.length ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="cartes10-tuiles">
                {lignes.map((c) => <Tuile key={c.inscription_id} c={c} reprise={d.reprise} onOuvrir={() => { setMessage(""); setPointe(null); setErreur(""); setOuverte(cle(c)); }} />)}
              </div>
            ) : <p className="text-sm text-[var(--muted)] py-6 text-center">Aucune carte dans ce filtre.</p>
          ) : (
            <Tableau
              entetes={["Élève", { label: "Cours", large: true }, "Paiement"]}
              lignes={lignes.map((c) => ({ ...c, id: c.inscription_id }))} vide="Aucune carte dans ce filtre."
              rendu={(c) => (
                <>
                  <Cellule fort><span className="whitespace-nowrap">{c.eleve}</span></Cellule>
                  <Cellule>{c.cours}<div className="text-[11px] text-[var(--muted)]">{c.jour}{c.fin ? ` · valable jusqu'au ${jour(c.fin)}` : ""}</div></Cellule>
                  <Cellule><Paiement p={c.paiement || {}} onAller={onAller} /></Cellule>
                </>
              )}
            />
          )}
        </div>
        {onAller && (
          <button type="button" className="mt-4 inline-flex items-center gap-1 text-sm underline text-[var(--muted)]"
            onClick={() => onAller({ onglet: "facturation", id: "especes" })} data-testid="cartes10-vers-cash">
            Encaisser au comptoir : Facturation › Cash / espèces <ArrowRight size={13} />
          </button>
        )}
      </Section>
      {groupe.length > 0 && (
        <Feuille groupe={groupe} occupe={occupe} erreur={erreur} pointe={pointe} onFermer={fermer} onAller={onAller}
          onPointer={pointer} onCases={cases} reprise={d.reprise} onReprendre={reprendre} onSuivante={suivante}
          onAnnuler={() => pointe && pointer(groupe.find((x) => x.courante) || groupe[0], pointe.le, !pointe.present).then(() => setPointe(null))} />
      )}
    </div>
  );
}
